'use client';

import {
	BlogCard,
	Button,
	Chip,
	Column,
	Flex,
	Heading,
	Icon,
	ImageWrapper,
	RichText,
	Row,
	Separator,
	ShareModal,
	Spacer,
	SpecialBlogCard,
} from 'daskapital-ds';
import { IPost, Taxonomy } from '@/lib/wordpress/_types/archiveGrid';
import { Suspense, useEffect, useState } from 'react';

import { BlocksProps } from '@/lib/wordpress/_types/blocksStyle';
import DisplayImage from '@/components/common/DisplayImage/DisplayImage';
import NavigationEvents from '@/components/common/NavigationEvents/NavigationEvents';
import displayBlock from '@/lib/wordpress/blocks/displayBlock';
import { formatDate } from '@/lib/helpers/dateFormate';
import { removeScroll } from '@/lib/helpers/scrollHelpers';
import { useGlobalContext } from '@/lib/globalContext';
import Link from 'next/link';

const BlogPost = (post: IPost) => {
	const state = useGlobalContext();
	const [shareOpen, setShareOpen] = useState(false);

	useEffect(() => {
		removeScroll(shareOpen, state);
	}, [shareOpen, state]);

	const categories = (post?.categories?.filter((c) => c.name === 'category')[0]?.items as unknown as Taxonomy[]) || [];

	const relatedArticles = post?.acf?.related_articles;

	return (
		<div>
			<Suspense>
				<NavigationEvents translatedURI={post?.translatedURI} acf={{ page_style: 'light' }} />
			</Suspense>
			<Spacer height={8} />
			<Row>
				<Column lg={2}></Column>
				<Column lg={8}>
					<ImageWrapper aspectRatioHeight={40}>
						<DisplayImage alt={post?.title as string} url={post?.image as string} fill={true} className="radius-md" />
					</ImageWrapper>
					<Flex align="center" gap="12px" className="mt-4">
						{categories &&
							categories?.map((item, i) => {
								return (
									<Chip key={`category-${i}`} variant="light">
										{item.name}
									</Chip>
								);
							})}

						<Heading tag="span" size="texts">
							{formatDate(post?.date as string, 'DATE_SHORT')}
						</Heading>
					</Flex>

					<Row fluid={true} align="center" className="mt-4">
						<Column lg={11}>
							<Heading tag="h1" size="h3">
								{post.title}
							</Heading>
						</Column>
						<Column lg={1} className={'text-md-end'}>
							<Button
								variant={'icon'}
								mode="dark"
								className="mt-4 justify-content-center"
								rightIcon={<Icon icon="icon-share" />}
								onClick={() => setShareOpen(true)}></Button>
						</Column>
					</Row>

					<Spacer height={6} />
					<Separator height="1px" color="var(--color-gray40)" />
					<Spacer height={6} />

					<div className="single-postcontent">
						{!!(post?.blocks as [])?.length &&
							(post?.blocks as []).map((block: BlocksProps, index: number) => {
								return displayBlock(block, index);
							})}
					</div>
					<Spacer height={8} />

					<div className="block-group radius-md bg-primary50 color-white p-6">
						<div>
							<Row align="center" fluid={true} mobileSpace={true}>
								<Column lg={8}>
									<Heading size="h3" tag="h5">
										{(state?.dict?.blog as Record<string, string>).shareTitle}
									</Heading>
									<RichText tag={'p'}>{(state?.dict?.blog as Record<string, string>).shareDesc}</RichText>
								</Column>
								<Column lg={4} className="text-md-end">
									<Button
										variant={'primary'}
										mode="dark"
										className="mt-4"
										rightIcon={<Icon icon="icon-forward-arrow" />}
										onClick={() => setShareOpen(true)}>
										{(state?.dict?.blog as Record<string, string>).shareBtn}
									</Button>
								</Column>
							</Row>
						</div>
					</div>
				</Column>
				<Column lg={2}></Column>
			</Row>

			<Spacer height={8} />

			{relatedArticles && relatedArticles.length > 0 && (
				<div className="block-group pt-8 pb-8">
					<Row>
						<Column lg={12}>
							<Heading tag="h4" size="h3" className="mb-4">
								{state?.dict?.blog?.related_articles}
							</Heading>
						</Column>
						{relatedArticles.map((related: IPost, i: number) => {
							const categorias = related && related.categories ? related?.categories[0].items : [];

							const imageElement = (
								<DisplayImage url={related?.image as string} alt={related?.title ? (related?.title as string) : ''} fill={true} />
							);

							return (
								<Column key={`related-${i}`} md={6} lg={3}>
									<Link href={related.slug || '#'}>
										<SpecialBlogCard
											date={formatDate(related?.date as string, 'DATE_SHORT')}
											categories={categorias?.map((c) => c.name)}
											imageElement={imageElement}
											title={related.title as string}
											description={related.excerpt as string}
											author={related.author as string}
										/>
									</Link>
								</Column>
							);
						})}
					</Row>
				</div>
			)}

			<ShareModal
				siteTitle={process.env.NEXT_PUBLIC_SITE_NAME}
				title={post.title}
				url={`${process.env.NEXT_PUBLIC_FE_URL}${post.slug}`}
				label={(state?.dict?.blog as Record<string, string>).shareBtn}
				copyText={(state?.dict?.blog as Record<string, string>).shareCopy}
				open={shareOpen}
				onClose={() => setShareOpen(false)}></ShareModal>
		</div>
	);
};
export default BlogPost;
